import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  X,
  Mail,
  Send,
  Users,
  Loader2,
  AlertTriangle,
  CheckCircle2,
  Clock,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const SendBoletimModal = ({ isOpen, onClose, session, members = [], onSavedPending }) => {
  const validMembers = members.filter((m) => m.email);
  const [selectedIds, setSelectedIds] = useState(new Set(validMembers.map((m) => m.id)));
  const [customMessage, setCustomMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [savingPending, setSavingPending] = useState(false);
  const [result, setResult] = useState(null);

  if (!isOpen || !session) return null;

  const toggle = (id) => {
    const next = new Set(selectedIds);
    next.has(id) ? next.delete(id) : next.add(id);
    setSelectedIds(next);
  };

  const toggleAll = () => {
    if (selectedIds.size === validMembers.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(validMembers.map((m) => m.id)));
    }
  };

  const handleSend = async () => {
    if (selectedIds.size === 0) {
      toast.error("Selecione ao menos um Irmão para envio.");
      return;
    }
    setSending(true);
    setResult(null);
    try {
      const res = await axios.post(`${API}/sessions/${session.id}/send-boletim`, {
        recipient_member_ids: Array.from(selectedIds),
        custom_message: customMessage.trim(),
      });
      setResult(res.data);
      toast.success(res.data.message);
    } catch (err) {
      const detail = err.response?.data?.detail || "Erro ao enviar boletim.";
      toast.error(detail);
      setResult({ error: detail });
    } finally {
      setSending(false);
    }
  };

  const handleSaveAsPending = async () => {
    if (selectedIds.size === 0) {
      toast.error("Selecione ao menos um Irmão para o rascunho.");
      return;
    }
    setSavingPending(true);
    setResult(null);
    try {
      const res = await axios.post(`${API}/boletins`, {
        session_id: session.id,
        recipient_member_ids: Array.from(selectedIds),
        custom_message: customMessage.trim(),
      });
      toast.success("Boletim salvo como PENDENTE. Você pode despachá-lo depois na aba Sessões.");
      setResult({ pending_saved: true, boletim: res.data, message: "Rascunho salvo como Pendente." });
      if (onSavedPending) onSavedPending(res.data);
    } catch (err) {
      const detail = err.response?.data?.detail || "Erro ao salvar boletim como pendente.";
      toast.error(detail);
      setResult({ error: detail });
    } finally {
      setSavingPending(false);
    }
  };

  const close = () => {
    setResult(null);
    setCustomMessage("");
    onClose();
  };

  return (
    <div
      data-testid="send-boletim-modal"
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={close}
    >
      <div
        className="w-full max-w-2xl max-h-[90vh] bg-[#0B111E] border border-[#D4AF37]/40 rounded-2xl shadow-2xl overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 py-5 border-b border-[#D4AF37]/20 bg-[#080D18] flex items-center justify-between shrink-0">
          <div>
            <div className="flex items-center space-x-2 text-xs text-[#D4AF37] uppercase tracking-widest font-bold">
              <Mail className="w-3.5 h-3.5" />
              <span>Chancelaria • Envio de Boletim</span>
            </div>
            <h2 className="text-lg font-serif font-bold text-white mt-1">
              Convocação — Sessão nº {session.session_number}
            </h2>
            <p className="text-xs text-slate-400 mt-0.5">
              {session.date} • {session.session_type}
            </p>
          </div>
          <button
            onClick={close}
            data-testid="boletim-modal-close"
            className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-white/5"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-5 overflow-y-auto">
          {/* Result View */}
          {result && result.sent_count !== undefined && (
            <div className="rounded-xl border border-emerald-500/40 bg-emerald-500/10 p-4 flex items-start space-x-3">
              <CheckCircle2 className="w-5 h-5 text-emerald-400 shrink-0 mt-0.5" />
              <div>
                <p className="text-sm text-emerald-300 font-semibold">{result.message}</p>
                {result.failed_count > 0 && (
                  <p className="text-xs text-rose-300 mt-1">
                    {result.failed_count} envio(s) com falha. Verifique os e-mails.
                  </p>
                )}
              </div>
            </div>
          )}

          {result?.error && (
            <div className="rounded-xl border border-rose-500/40 bg-rose-500/10 p-4 flex items-start space-x-3">
              <AlertTriangle className="w-5 h-5 text-rose-400 shrink-0 mt-0.5" />
              <div>
                <p className="text-sm text-rose-300 font-semibold">Erro ao enviar</p>
                <p className="text-xs text-rose-200 mt-1">{result.error}</p>
              </div>
            </div>
          )}

          {/* Custom Message */}
          <div>
            <label className="text-[10px] uppercase text-slate-400 tracking-wider font-semibold">
              Mensagem adicional do Venerável (opcional)
            </label>
            <textarea
              data-testid="boletim-input-custom-message"
              rows={3}
              value={customMessage}
              onChange={(e) => setCustomMessage(e.target.value)}
              placeholder="Escreva uma observação ou lembrança fraternal para os Irmãos..."
              className="mt-1 w-full bg-[#131C2E] border border-slate-700 rounded-lg px-3 py-2 text-sm text-white focus:border-[#D4AF37] outline-none resize-none"
            />
          </div>

          {/* Recipients */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-[10px] uppercase text-slate-400 tracking-wider font-semibold flex items-center space-x-1.5">
                <Users className="w-3 h-3" />
                <span>Destinatários ({selectedIds.size} / {validMembers.length})</span>
              </label>
              <button
                data-testid="boletim-btn-toggle-all"
                onClick={toggleAll}
                className="text-[10px] uppercase font-bold text-[#D4AF37] hover:text-[#F5C542] tracking-wider"
              >
                {selectedIds.size === validMembers.length ? "Desmarcar todos" : "Selecionar todos"}
              </button>
            </div>
            <div className="max-h-56 overflow-y-auto rounded-lg border border-slate-700 bg-[#131C2E] divide-y divide-slate-800/80">
              {validMembers.map((m) => (
                <label
                  key={m.id}
                  data-testid={`boletim-recipient-${m.id}`}
                  className="flex items-center space-x-3 px-4 py-2.5 cursor-pointer hover:bg-white/5"
                >
                  <input
                    type="checkbox"
                    checked={selectedIds.has(m.id)}
                    onChange={() => toggle(m.id)}
                    className="accent-[#D4AF37]"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-white truncate">{m.name}</div>
                    <div className="text-xs text-slate-400 truncate">{m.email} • {m.degree}</div>
                  </div>
                </label>
              ))}
              {validMembers.length === 0 && (
                <div className="px-4 py-6 text-center text-xs text-slate-500">
                  Nenhum Irmão com e-mail cadastrado.
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer Actions */}
        <div className="px-6 py-4 border-t border-slate-800/80 bg-[#080D18] flex items-center justify-between shrink-0 gap-2">
          <button
            onClick={close}
            data-testid="boletim-btn-cancel"
            className="text-xs uppercase tracking-wider text-slate-400 hover:text-white font-semibold"
          >
            Cancelar
          </button>
          <div className="flex items-center gap-2">
            <button
              data-testid="boletim-btn-save-pending"
              onClick={handleSaveAsPending}
              disabled={savingPending || sending || selectedIds.size === 0}
              title="Salvar rascunho para despachar depois (útil quando SMTP ainda não está configurado)"
              className="flex items-center space-x-2 bg-[#131C2E] hover:bg-[#1E293B] border border-amber-500/40 text-amber-300 disabled:opacity-40 disabled:cursor-not-allowed font-bold px-4 py-2.5 rounded-xl text-xs uppercase tracking-wider transition-all"
            >
              {savingPending ? (
                <>
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  <span>Salvando...</span>
                </>
              ) : (
                <>
                  <Clock className="w-3.5 h-3.5" />
                  <span>Salvar como Pendente</span>
                </>
              )}
            </button>
            <button
              data-testid="boletim-btn-send"
              onClick={handleSend}
              disabled={sending || savingPending || selectedIds.size === 0}
              className="flex items-center space-x-2 bg-[#D4AF37] hover:bg-[#C5A028] disabled:bg-slate-700 disabled:text-slate-400 disabled:cursor-not-allowed text-slate-950 font-bold px-5 py-2.5 rounded-xl text-xs uppercase tracking-wider transition-all shadow-[0_0_15px_rgba(212,175,55,0.25)]"
            >
              {sending ? (
                <>
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  <span>Enviando...</span>
                </>
              ) : (
                <>
                  <Send className="w-3.5 h-3.5" />
                  <span>Enviar Boletim ({selectedIds.size})</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
